import React, { useEffect, useRef, useState } from 'react';
import AppStorage from '@Services/AppStorage';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Banner } from '@els/els-react--banner';
import { LinkElement } from '@els/els-react--link-element';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import Result from '@Components/Results/Result';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import InfoTooltipOnClick from '@Components/InfoTooltip/InfoTooltipOnClick/InfoTooltipOnClick';
import StructureZoomService from '@Services/StructureZoom';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import store from '@Store';
import { SORT_BY_SIMILARITY_FIELD, substances } from '@Constants';

import './ResultsList.scss';

const SIMILARITY_SCORE_INFO_URL = 'https://www.elsevier.support/reaxys/answer/how-is-the-substance-similarity-score-calculated';

function getResultListItemKey(result = {}, metadata = {}) {
  const resultSetKey = [metadata.databaseId, metadata.context, metadata.searchId, metadata.resultName, metadata.hitSetKey]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(':');
  const resultIdentity = result.id || result.viewId || result.details?.id || result.index;

  return [resultSetKey, resultIdentity, result.index].filter((value) => value !== undefined && value !== null && value !== '').join('|');
}

function SimilarityScoreBanner({ onClose }) {
  return (
    <Banner
      type="info"
      isVisible
      size="small"
      hideCloseLabel
      onClose={onClose}
      className="substance-similarity-score-banner"
      link={
        <LinkElement href={SIMILARITY_SCORE_INFO_URL} data-e2e="similarity-score-info-link" isExternal>
          How is similarity score calculated?
        </LinkElement>
      }>
      Results are sorted by structural similarity to your query. Each result includes a substance similarity score.
    </Banner>
  );
}

function shouldShowSimilarityScoreBanner({ context, gridView, results, sort }) {
  const firstResult = results[0];

  return (
    context === substances &&
    !gridView &&
    sort === SORT_BY_SIMILARITY_FIELD &&
    String(firstResult?.index) === '1' &&
    !!firstResult?.molImage?.structure &&
    firstResult?.similarityPercentage >= 0
  );
}

function ResultsList({
  size,
  results,
  gridView,
  metadata,
  context,
  updateItemChecked = () => {},
  searchType,
  originalSearchType,
  hideCheckbox,
  currentPage,
  totalPages,
}) {
  const [isSimilarityBannerVisible, setIsSimilarityBannerVisible] = useState(true);
  const [zoom, setZoom] = useState(() => {
    const stored = AppStorage.instance.getFromSessionStorage('zoom');
    return typeof stored === 'number' ? stored : 0;
  });
  const listRef = useRef(null);

  useEffect(() => {
    AppStorage.instance.setToSessionStorage('zoom', zoom);
    StructureZoomService.subscribe(
      context,
      (zoomVal) => {
        setZoom(zoomVal);
        AppStorage.instance.setToSessionStorage('zoom', zoomVal);
      },
      true
    );

    return () => {
      StructureZoomService.unsubscribe(context);
    };
  }, [context, zoom]);

  const zoomLevels = StructureZoomService.getZoomClasses('default') || [];

  if (!results.length) return null;

  const ariaLabel = currentPage != null && totalPages != null ? `Results page ${currentPage} of ${totalPages}` : 'Results List';

  const containerProps = {
    ref: listRef,
    className: cn(
      'e2e-results-list',
      `rx-list--${context}`,
      `result-list-zoom--${zoomLevels[zoom]}`,
      metadata.heatmapMode ? 'rx-list-heatmap' : '',
      'rx-focusable',
      'rx-list-react'
    ),
    'aria-label': ariaLabel,
    tabIndex: -1,
  };

  const tooltipId = Tooltip.TooltipConstants.ids.ResultsListComponent;
  const { sort } = getCombinedRouteParams(store.getState());
  const showSimilarityBanner = isSimilarityBannerVisible && shouldShowSimilarityScoreBanner({ context, gridView, results, sort });

  const anySubstanceHasHitData = results.some((result) => result?.facts?.some((fact) => fact.hitData));

  const resultProps = {
    metadata,
    context,
    updateItemChecked,
    searchType,
    originalSearchType,
    gridView,
    hideCheckbox,
    size,
    zoom,
    anySubstanceHasHitData,
  };

  return (
    <>
      {showSimilarityBanner && <SimilarityScoreBanner onClose={() => setIsSimilarityBannerVisible(false)} />}
      <ul {...containerProps}>
        {results.map((result) => (
          <li className="e2e-results-list-item rx-list-item" key={getResultListItemKey(result, metadata)}>
            <Result result={result} {...resultProps} />
          </li>
        ))}
      </ul>
      <Tooltip id={TooltipConstants.ids.ResultStructureComponent} />
      <Tooltip id={tooltipId} />
      <InfoTooltipOnClick id={TooltipConstants.ids.ResultFilterComponent} place="right" className="centerArrow" />
    </>
  );
}

ResultsList.propTypes = {
  metadata: MetadataPropTypesShape,
  context: PropTypes.string.isRequired,
  updateItemChecked: PropTypes.func,
  results: PropTypes.array.isRequired,
  size: PropTypes.number.isRequired,
  gridView: PropTypes.bool,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  hideCheckbox: PropTypes.bool,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};

SimilarityScoreBanner.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ResultsList;
